import React from "react";
import { Alert, Divider, List, Typography } from "antd";
import { InvalidReasonDisplay } from "../../vault/ui/InvalidReasonDisplay";
import dayjs from "dayjs";
import {
  statusColors,
  statusMapping,
} from "../fixtures/folderHistoryFixtures.tsx";
import {
  FolderChange,
  HistoryRecord,
} from "../../../redux/interfaces/folder.types.ts";
import { HistoryFolderStatus } from "./HistoryFolderStatus.tsx";
import { HistoryItemActive } from "./HistoryItemActive.tsx";
import { HistoryAssignedUser } from "./HistoryAssignedUser.tsx";
import { HistoryCollaborator } from "./HistoryCollaborator.tsx";
import { HistoryAction } from "./HistoryAction.tsx";
import { HistoryFieldChanges } from "./HistoryFieldChanges.tsx";

// Fixed - only one Props interface
interface Props {
  record: HistoryRecord;
}

const prefillSources = {
  identity_ocr: "OCR de identificación",
  confirmed_data: "Datos confirmados por el usuario",
  proof_of_address_ocr: "OCR de comprobante de domicilio",
};

export const HistoryItem: React.FC<Props> = ({ record }) => {
  const dateFormat = "DD-MM-YYYY";
  const timeFormat = "HH:mm:ss";

  // Map from new response format to what the existing components expect
  const prev_status = record.previous_status;
  const actual_status = record.new_status;
  const resource = record.resource_type;
  const author = record.author;
  const role = record.role;
  const prefill = record.extra_data?.prefill;

  const actionType = (): FolderChange["action"] => {
    switch (record.action) {
      case "created":
        return "created";
      case "deleted":
        return "deleted";
      case "updated":
        return "updated";
      case "status_changed":
        return "status_changed";
      case "assigned":
        return "assigned";
      case "uploaded":
        return "uploaded";
      default:
        return "created"; // Default fallback
    }
  };

  // Create a compatible change object for existing components
  const change: FolderChange = {
    name: record.name,
    resource: resource,
    author: author,
    role: role,
    action: actionType(),
    timestamp: record.created_at,
    status: {
      previous: prev_status,
      actual: actual_status,
    },
    extra_data: record.extra_data,
  };

  // Only add these optional properties if they have values
  if (record.previous_is_active !== null || record.new_is_active !== null) {
    change.is_active = {
      previous: record.previous_is_active,
      actual: record.new_is_active,
    };
  }

  if (
    record.previous_assigned_user !== null ||
    record.new_assigned_user !== null
  ) {
    change.assigned_user = {
      previous: record.previous_assigned_user,
      actual: record.new_assigned_user,
    };
  }

  if (
    record.previous_collaborator !== null ||
    record.new_collaborator !== null
  ) {
    change.collaborator = {
      previous: record.previous_collaborator,
      actual: record.new_collaborator,
    };
  }

  const prevColor = statusColors[resource]?.[prev_status!] || null;
  const actualColor = statusColors[resource]?.[actual_status!] || null;

  const prevIcon = statusMapping[resource]?.[prev_status!]?.icon || null;
  const actualIcon = statusMapping[resource]?.[actual_status!]?.icon || null;

  const prevText = statusMapping[resource]?.[prev_status!]?.text || null;
  const actualText = statusMapping[resource]?.[actual_status!]?.text || null;

  const date = dayjs(change.timestamp).format(dateFormat);
  const time = dayjs(change.timestamp).format(timeFormat);

  const status = (
    <HistoryFolderStatus
      actual_status={actual_status}
      prev_status={prev_status}
      prevColor={prevColor!}
      prevIcon={prevIcon!}
      prevText={prevText!}
      actualColor={actualColor!}
      actualIcon={actualIcon!}
      actualText={actualText!}
    />
  );

  const active = <HistoryItemActive change={change} />;

  return (
    <List.Item
      key={change.timestamp}
      className="bg-white rounded-lg shadow-sm mb-4 p-4"
    >
      <div className="flex flex-col space-y-2">
        <Typography.Text strong className="text-lg my-1">
          {change.name}
        </Typography.Text>
        <Divider />

        <Typography.Text className="font-medium my-1">
          Recurso: {resource}
        </Typography.Text>

        {author && (
          <Typography.Text className="font-medium my-1">
            Autor: {author}
          </Typography.Text>
        )}

        {role && (
          <Typography.Text className="font-medium my-1">
            Rol: {role}
          </Typography.Text>
        )}

        {prefill ? (
          <Alert
            message={
              prefill.skipped
                ? "Prellenado omitido: formulario validado"
                : "Prellenado aplicado"
            }
            description={
              <div>
                <div>
                  Origen: {prefillSources[prefill.source] ?? prefill.source}
                </div>
                <div>Documento fuente: {prefill.source_requirement_name}</div>
                {prefill.skipped ? (
                  <div>Los datos del formulario no se modificaron.</div>
                ) : null}
              </div>
            }
            type="info"
            showIcon
          />
        ) : (
          change?.action && <HistoryAction action={change.action} />
        )}

        {change.assigned_user && <HistoryAssignedUser change={change} />}

        {change.collaborator && <HistoryCollaborator change={change} />}

        {active}
        {prefill ? null : status}

        {change.extra_data?.invalid_reason && (
          <Alert
            message="Requisito inválido"
            description={
              <div className="flex items-start">
                <InvalidReasonDisplay
                  reason={
                    Array.isArray(change.extra_data.invalid_reason)
                      ? change.extra_data.invalid_reason.join("\n")
                      : change.extra_data.invalid_reason
                  }
                />
              </div>
            }
            type="error"
            className="mt-2 mb-2"
            showIcon
          />
        )}

        <div className="text-gray-500">
          <Typography.Text className="block font-medium">
            Fecha: {date}
          </Typography.Text>
          <Typography.Text className="block font-medium">
            Hora: {time}
          </Typography.Text>
        </div>

        {!prefill?.skipped && change.extra_data?.changed_fields?.length ? (
          <HistoryFieldChanges changes={change.extra_data.changed_fields} />
        ) : null}
      </div>
    </List.Item>
  );
};
